import { parseBritishDate } from "./insightDates";

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const BRITISH_DATE_ONLY_PATTERN = /^\d{2}-\d{2}-\d{4}$/;
const BRITISH_DATE_TIME_PATTERN = /^(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2})(?::(\d{2}))?$/;
const ISO_DATE_PREFIX_PATTERN = /^\d{4}-\d{2}-\d{2}(?:$|T)/;

const DISPLAY_DATE_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const DISPLAY_DATE_TIME_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

const DISPLAY_DATE_UTC_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: "UTC",
});

const DISPLAY_DATE_TIME_UTC_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
  timeZone: "UTC",
});

const DISPLAY_DATE_TIME_SECONDS_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

const DISPLAY_DATE_TIME_SECONDS_UTC_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
  timeZone: "UTC",
});

type DateDisplayInput = string | Date | null | undefined;

type DateTimeFormatter = Intl.DateTimeFormat;

function formattedPart(
  formatter: DateTimeFormatter,
  date: Date,
  type: Intl.DateTimeFormatPartTypes
) {
  return formatter.formatToParts(date).find((part) => part.type === type)?.value ?? "";
}

function formatBritishDate(formatter: DateTimeFormatter, date: Date): string {
  const day = formattedPart(formatter, date, "day");
  const month = formattedPart(formatter, date, "month");
  const year = formattedPart(formatter, date, "year");
  return `${day}-${month}-${year}`;
}

function formatBritishDateTime(
  formatter: DateTimeFormatter,
  date: Date,
  includeSeconds: boolean
): string {
  const datePart = formatBritishDate(formatter, date);
  const hour = formattedPart(formatter, date, "hour");
  const minute = formattedPart(formatter, date, "minute");
  const second = includeSeconds ? `:${formattedPart(formatter, date, "second")}` : "";
  return `${datePart} ${hour}:${minute}${second}`;
}

export const DateDisplayFormat = {
  Date: "date",
  DateTime: "date-time",
} as const;

export type DateDisplayFormat = (typeof DateDisplayFormat)[keyof typeof DateDisplayFormat];

function parseDateOnlyLocal(value: string): Date | null {
  const match = DATE_ONLY_PATTERN.exec(value);
  if (match == null) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(year, month - 1, day);

  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null;
  }

  return parsed;
}

function parseDateOnlyUtc(value: string): Date | null {
  const match = DATE_ONLY_PATTERN.exec(value);
  if (match == null) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));

  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return null;
  }

  return parsed;
}

function parseBritishDateTime(value: string, dateOnlyAsUtc: boolean): Date | null {
  const match = BRITISH_DATE_TIME_PATTERN.exec(value);
  if (match == null) {
    return null;
  }

  const [, dayText, monthText, yearText, hourText, minuteText, secondText] = match;
  const day = Number(dayText);
  const month = Number(monthText);
  const year = Number(yearText);
  const hour = Number(hourText);
  const minute = Number(minuteText);
  const second = Number(secondText ?? "0");
  const parsedDate = parseBritishDate(`${dayText}-${monthText}-${yearText}`);

  if (parsedDate == null || hour > 23 || minute > 59 || second > 59) {
    return null;
  }

  const parsed = dateOnlyAsUtc
    ? new Date(
        Date.UTC(
          parsedDate.getUTCFullYear(),
          parsedDate.getUTCMonth(),
          parsedDate.getUTCDate(),
          hour,
          minute,
          second
        )
      )
    : new Date(
        parsedDate.getUTCFullYear(),
        parsedDate.getUTCMonth(),
        parsedDate.getUTCDate(),
        hour,
        minute,
        second
      );

  const matchesDate = dateOnlyAsUtc
    ? parsed.getUTCDate() === day &&
      parsed.getUTCMonth() + 1 === month &&
      parsed.getUTCFullYear() === year
    : parsed.getDate() === day && parsed.getMonth() + 1 === month && parsed.getFullYear() === year;

  return matchesDate ? parsed : null;
}

function coerceDate(value: DateDisplayInput, dateOnlyAsUtc: boolean): Date | null {
  if (value == null) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const normalized = value.trim();
  if (normalized.length === 0) {
    return null;
  }

  const britishDateTime = parseBritishDateTime(normalized, dateOnlyAsUtc);
  if (britishDateTime != null) {
    return britishDateTime;
  }

  const dateOnly = dateOnlyAsUtc ? parseDateOnlyUtc(normalized) : parseDateOnlyLocal(normalized);
  if (dateOnly != null) {
    return dateOnly;
  }

  // DD-MM-YYYY must never reach `new Date(string)`, which would read it month-first.
  if (BRITISH_DATE_ONLY_PATTERN.test(normalized)) {
    const british = parseBritishDate(normalized);
    if (british == null) return null;
    return dateOnlyAsUtc
      ? british
      : new Date(british.getUTCFullYear(), british.getUTCMonth(), british.getUTCDate());
  }

  // V8 silently rolls impossible ISO timestamps (2026-02-30T..) into the next month.
  if (
    ISO_DATE_PREFIX_PATTERN.test(normalized) &&
    parseDateOnlyUtc(normalized.slice(0, 10)) == null
  ) {
    return null;
  }

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function fallbackDisplayValue(value: DateDisplayInput, fallback: string): string {
  if (typeof value === "string") {
    const trimmed = value.trim();
    // An unparseable ISO-looking value (e.g. 2026-02-30) must not leak to users.
    return trimmed.length > 0 && !ISO_DATE_PREFIX_PATTERN.test(trimmed) ? trimmed : fallback;
  }

  return fallback;
}

export function formatDisplayDate(
  value: DateDisplayInput,
  options: {
    format?: DateDisplayFormat;
    fallback?: string;
    timeZone?: "UTC";
    includeSeconds?: boolean;
  } = {}
): string {
  const fallback = options.fallback ?? "—";
  const parsed = coerceDate(value, options.timeZone === "UTC");

  if (parsed == null) {
    return fallbackDisplayValue(value, fallback);
  }

  if (options.format === DateDisplayFormat.DateTime) {
    const formatter =
      options.timeZone === "UTC"
        ? options.includeSeconds
          ? DISPLAY_DATE_TIME_SECONDS_UTC_FORMATTER
          : DISPLAY_DATE_TIME_UTC_FORMATTER
        : options.includeSeconds
          ? DISPLAY_DATE_TIME_SECONDS_FORMATTER
          : DISPLAY_DATE_TIME_FORMATTER;
    return formatBritishDateTime(formatter, parsed, options.includeSeconds ?? false);
  }

  return options.timeZone === "UTC"
    ? formatBritishDate(DISPLAY_DATE_UTC_FORMATTER, parsed)
    : formatBritishDate(DISPLAY_DATE_FORMATTER, parsed);
}

export function formatDisplayDateTime(
  value: DateDisplayInput,
  options: { fallback?: string; timeZone?: "UTC"; includeSeconds?: boolean } = {}
): string {
  const fallback = options.fallback ?? "—";
  const parsed = coerceDate(value, options.timeZone === "UTC");

  if (parsed == null) {
    return fallbackDisplayValue(value, fallback);
  }

  return formatDisplayDate(value, { ...options, format: DateDisplayFormat.DateTime });
}

/** Formats date-only input as DD-MM-YYYY and timestamp input with UK 24-hour time. */
export function formatDisplayDateValue(
  value: DateDisplayInput,
  options: { fallback?: string; timeZone?: "UTC"; includeSeconds?: boolean } = {}
): string {
  const text = typeof value === "string" ? value.trim() : "";
  const dateOnly = /^(?:\d{4}-\d{2}-\d{2}|\d{2}-\d{2}-\d{4})$/.test(text);
  return dateOnly ? formatDisplayDate(value, options) : formatDisplayDateTime(value, options);
}
